import {
  ReportDatePickerValue,
  TicketReportingFilterDetail,
  TicketReportingFilterKey,
  TicketReportingFilterValues,
} from '@vision/ui/interfaces';

export const EMPTY_TICKET_REPORTING_FILTERS: TicketReportingFilterValues = {
  selectedTicketAssigneeIds: [],
  selectedTicketChannelIds: [],
  selectedTicketClosedDate: null,
  selectedTicketCustomFieldCodes: [],
  selectedTicketPoolIds: [],
  selectedTicketPriorities: [],
  selectedTicketProjectIds: [],
  selectedTicketRootCauseIds: [],
  selectedTicketStatusIds: [],
  selectedTicketTeamIds: [],
};

export const TICKET_REPORTING_FILTER_STATE_KEY_MAP: Partial<
  Record<TicketReportingFilterKey, keyof TicketReportingFilterValues>
> = {
  'ticket-status': 'selectedTicketStatusIds',
  'ticket-priority': 'selectedTicketPriorities',
  'ticket-root-cause': 'selectedTicketRootCauseIds',
  'ticket-pool': 'selectedTicketPoolIds',
  'ticket-team': 'selectedTicketTeamIds',
  'ticket-assignee': 'selectedTicketAssigneeIds',
  'ticket-channel': 'selectedTicketChannelIds',
  'ticket-project': 'selectedTicketProjectIds',
  'ticket-custom-fields': 'selectedTicketCustomFieldCodes',
};

export const TICKET_REPORTING_FILTER_KEYS = [
  'ticket-status',
  'ticket-priority',
  'ticket-root-cause',
  'ticket-pool',
  'ticket-team',
  'ticket-assignee',
  'ticket-channel',
  'ticket-project',
  'ticket-closed-date',
  'ticket-custom-fields',
] as TicketReportingFilterKey[];

export function isTicketReportingFilterKey(value: unknown): value is TicketReportingFilterKey {
  return typeof value === 'string' && TICKET_REPORTING_FILTER_KEYS.includes(value as TicketReportingFilterKey);
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function dateRange(value: unknown): ReportDatePickerValue | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as Partial<ReportDatePickerValue>;
  if (typeof candidate.fromDate !== 'string' || typeof candidate.toDate !== 'string') {
    return null;
  }

  return {
    fromDate: candidate.fromDate,
    toDate: candidate.toDate,
    ...(candidate.dateRange ? { dateRange: candidate.dateRange } : {}),
  };
}

export function normalizeTicketReportingFilters(value: unknown): TicketReportingFilterValues {
  const source = value && typeof value === 'object' ? (value as Partial<TicketReportingFilterValues>) : {};

  return {
    selectedTicketAssigneeIds: stringArray(source.selectedTicketAssigneeIds),
    selectedTicketChannelIds: stringArray(source.selectedTicketChannelIds),
    selectedTicketClosedDate: dateRange(source.selectedTicketClosedDate),
    selectedTicketCustomFieldCodes: stringArray(source.selectedTicketCustomFieldCodes),
    selectedTicketPoolIds: stringArray(source.selectedTicketPoolIds),
    selectedTicketPriorities: stringArray(source.selectedTicketPriorities),
    selectedTicketProjectIds: stringArray(source.selectedTicketProjectIds),
    selectedTicketRootCauseIds: stringArray(source.selectedTicketRootCauseIds),
    selectedTicketStatusIds: stringArray(source.selectedTicketStatusIds),
    selectedTicketTeamIds: stringArray(source.selectedTicketTeamIds),
  };
}

export function normalizeTicketReportingFilterKeys(value: unknown): TicketReportingFilterKey[] {
  return Array.isArray(value) ? value.filter(isTicketReportingFilterKey) : [];
}

export function deserializeTicketReportingFilters(
  detail?: TicketReportingFilterDetail | null,
): TicketReportingFilterValues {
  return {
    selectedTicketAssigneeIds: stringArray(detail?.assignee_ids),
    selectedTicketChannelIds: stringArray(detail?.channel_ids),
    selectedTicketClosedDate:
      typeof detail?.closed_from_date === 'string' && typeof detail.closed_to_date === 'string'
        ? { fromDate: detail.closed_from_date, toDate: detail.closed_to_date }
        : null,
    selectedTicketCustomFieldCodes: Array.isArray(detail?.custom_fields)
      ? detail.custom_fields.flatMap((field) => (typeof field?.field_code === 'string' ? [field.field_code] : []))
      : [],
    selectedTicketPoolIds: stringArray(detail?.ticket_pool_id),
    selectedTicketPriorities: stringArray(detail?.priority),
    selectedTicketProjectIds: stringArray(detail?.project_id),
    selectedTicketRootCauseIds: stringArray(detail?.root_cause_ids),
    selectedTicketStatusIds: stringArray(detail?.status_ids),
    selectedTicketTeamIds: stringArray(detail?.owner_id),
  };
}

export function getConfiguredTicketReportingFilterKeys(
  filtersProp: TicketReportingFilterValues,
): TicketReportingFilterKey[] {
  const filters = normalizeTicketReportingFilters(filtersProp);
  return TICKET_REPORTING_FILTER_KEYS.filter((key) => countTicketReportingFilterValues(key, filters) > 0);
}

export function serializeTicketReportingFilters(
  filtersProp: TicketReportingFilterValues,
  keys: TicketReportingFilterKey[],
): TicketReportingFilterDetail {
  const filters = normalizeTicketReportingFilters(filtersProp);
  const selectedKeys = new Set(normalizeTicketReportingFilterKeys(keys));
  const values = <K extends keyof TicketReportingFilterValues>(filterKey: TicketReportingFilterKey, stateKey: K) =>
    selectedKeys.has(filterKey) ? (filters[stateKey] as string[]) : [];
  const teamIds = values('ticket-team', 'selectedTicketTeamIds');
  const closedDate = selectedKeys.has('ticket-closed-date') ? filters.selectedTicketClosedDate : null;

  return {
    assignee_ids: values('ticket-assignee', 'selectedTicketAssigneeIds'),
    channel_ids: values('ticket-channel', 'selectedTicketChannelIds'),
    closed_from_date: closedDate?.fromDate,
    closed_to_date: closedDate?.toDate,
    custom_fields: values('ticket-custom-fields', 'selectedTicketCustomFieldCodes').map((field_code) => ({
      field_code,
    })),
    owner_id: teamIds,
    owner_types: teamIds.length ? ['InnerLoop::TicketTeam'] : undefined,
    priority: values('ticket-priority', 'selectedTicketPriorities'),
    project_id: values('ticket-project', 'selectedTicketProjectIds'),
    root_cause_ids: values('ticket-root-cause', 'selectedTicketRootCauseIds'),
    status_ids: values('ticket-status', 'selectedTicketStatusIds'),
    ticket_pool_id: values('ticket-pool', 'selectedTicketPoolIds'),
  };
}

export function applyTicketReportingMainFilters({
  widgetKeys,
  widgetFilters,
  mainFilters,
}: {
  widgetKeys: TicketReportingFilterKey[];
  widgetFilters: TicketReportingFilterValues;
  mainFilters: TicketReportingFilterValues;
}): { filterKeys: TicketReportingFilterKey[]; filters: TicketReportingFilterValues } {
  const normalizedWidgetFilters = normalizeTicketReportingFilters(widgetFilters);
  const normalizedMainFilters = normalizeTicketReportingFilters(mainFilters);
  const mainFilterKeys = new Set(getConfiguredTicketReportingFilterKeys(normalizedMainFilters));
  const widgetFilterKeys = new Set(normalizeTicketReportingFilterKeys(widgetKeys));
  let filters = { ...normalizedWidgetFilters };

  TICKET_REPORTING_FILTER_KEYS.forEach((filterKey) => {
    if (!mainFilterKeys.has(filterKey)) {
      return;
    }

    filters = clearTicketReportingFilter(filters, filterKey);

    if (filterKey === 'ticket-closed-date') {
      filters.selectedTicketClosedDate = normalizedMainFilters.selectedTicketClosedDate;
      return;
    }

    const stateKey = TICKET_REPORTING_FILTER_STATE_KEY_MAP[filterKey];
    if (stateKey) {
      filters[stateKey] = normalizedMainFilters[stateKey] as never;
    }
  });

  return {
    filterKeys: TICKET_REPORTING_FILTER_KEYS.filter(
      (filterKey) => mainFilterKeys.has(filterKey) || widgetFilterKeys.has(filterKey),
    ),
    filters,
  };
}

export function clearTicketReportingFilter(
  filters: TicketReportingFilterValues,
  filterKey: TicketReportingFilterKey,
): TicketReportingFilterValues {
  const nextFilters = normalizeTicketReportingFilters(filters);

  if (filterKey === 'ticket-closed-date') {
    nextFilters.selectedTicketClosedDate = null;
    return nextFilters;
  }

  const stateKey =
    TICKET_REPORTING_FILTER_STATE_KEY_MAP[filterKey as keyof typeof TICKET_REPORTING_FILTER_STATE_KEY_MAP];
  if (stateKey) {
    nextFilters[stateKey] = [] as never;
  }

  return nextFilters;
}

export function countConfiguredTicketReportingFilters(
  keys: TicketReportingFilterKey[],
  filters: TicketReportingFilterValues,
): number {
  return keys.filter((key) => countTicketReportingFilterValues(key, filters) > 0).length;
}

export function countTicketReportingFilterValues(
  key: TicketReportingFilterKey,
  filtersProp: TicketReportingFilterValues,
): number {
  const filters = normalizeTicketReportingFilters(filtersProp);

  if (key === 'ticket-closed-date') {
    return filters.selectedTicketClosedDate ? 1 : 0;
  }

  const stateKey = TICKET_REPORTING_FILTER_STATE_KEY_MAP[key as keyof typeof TICKET_REPORTING_FILTER_STATE_KEY_MAP];
  return stateKey ? (filters[stateKey] as string[]).length : 0;
}
